// src/lib/i18n/useI18n.ts
// ─────────────────────────────────────────────────────────────────────────
// Sprint: Frontend UI i18n Foundation V1, Parte A.
//
// Thin layer on top of usePreferences() — never a second, independent
// language state (usePreferences' own module-level external store already
// makes `language` reactive/shared across every mount point; this hook
// just reads it and hands back a typed `t()`). No provider/context needed:
// usePreferences() is already global.
import { useCallback } from "react";
import { usePreferences } from "@/lib/hooks/usePreferences";
import { FALLBACK_LANGUAGE, isSupportedLanguage, type Language } from "./languages";
import { MESSAGES, type MessageKey } from "./messages";

export type TranslateParams = Record<string, string | number>;

function interpolate(template: string, params?: TranslateParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

/**
 * Pure translate function — usable outside a component (tests, non-hook
 * helpers). `language` is defensively re-validated here too (never assumes
 * the caller already checked) since usePreferences' own `language` is
 * normally already-valid, but this function may also be called with a
 * plain string from a less-trusted source.
 */
export function translate(language: Language | string, key: MessageKey, params?: TranslateParams): string {
  const lang = isSupportedLanguage(language) ? language : FALLBACK_LANGUAGE;
  const template = MESSAGES[lang]?.[key] ?? MESSAGES[FALLBACK_LANGUAGE][key] ?? key;
  return interpolate(template, params);
}

export function useI18n(): { language: Language; t: (key: MessageKey, params?: TranslateParams) => string } {
  const { language } = usePreferences();
  const safeLanguage = isSupportedLanguage(language) ? language : FALLBACK_LANGUAGE;
  const t = useCallback(
    (key: MessageKey, params?: TranslateParams) => translate(safeLanguage, key, params),
    [safeLanguage],
  );
  return { language: safeLanguage, t };
}
